import {useCallback, useEffect, useMemo, useState} from 'react';
import {Platform} from 'react-native';
import {getConfig} from '@shared/config/AppConfig';
import {NovaVoiceModule} from '@shared/nativeModules/NovaVoiceModule';
import {NovaWakeWordSwitch} from '../NovaAIProps';
import {isWakeWordSupported, useWakeWord} from './useWakeWord';
import {WAKE_WORD_ENGINE_LABELS, WakeWordEngineName} from './wakeWordConfig';
import {
  loadWakeWordEngine,
  loadWakeWordSwitch,
  saveWakeWordEngine,
  saveWakeWordSwitch,
} from './wakeWordSwitch';

/**
 * The engines a tester can pick between: openWakeWord wherever Hey Nova is
 * offered, and Picovoice in the browser and on iOS where its AccessKey is
 * set — there is no Android keyword file.
 */
const offeredEngines = (): WakeWordEngineName[] =>
  (Platform.OS === 'web' || Platform.OS === 'ios') &&
  getConfig()?.NOVA_AI_PICOVOICE_ACCESS_KEY
    ? ['openwakeword', 'picovoice']
    : ['openwakeword'];

/**
 * How long after Nova stops talking before Hey Nova listens again: the tail of
 * the last word is still coming off the walls when the audio ends (as
 * NovaAIChatWindow's SPEECH_TAIL_MS, for its own microphone).
 */
const SPEECH_TAIL_MS = 400;

/**
 * How long Hey Nova stays aside once the chat window has taken a wake, for
 * the window to say it is busy. It says so a render later, and in that render
 * nothing else holds the wake word back: on a phone its microphone would open
 * again just as the window's recogniser starts.
 */
const HANDOVER_MS = 1000;

/**
 * Hey Nova for a Nova container — the web's (NovaAI.web.tsx) and the phone's
 * (NovaAI.tsx). It listens there, not in the chat window, because the window
 * unmounts when it closes.
 *
 * Offered where the environment turns it on (NOVA_AI_WAKE_WORD) and the
 * browser or build can do it; listening only once the technician switches it
 * on, a choice kept on the device. It steps aside while the chat's own mic is
 * open, while a question is being answered and while Nova is talking, so it
 * never wakes on Nova's own voice.
 *
 * `openForWake` opens the chat window, restored: a stable callback.
 */
export const useHeyNova = (openForWake: () => void) => {
  const offered = useMemo(
    () => Boolean(getConfig()?.NOVA_AI_WAKE_WORD) && isWakeWordSupported(),
    [],
  );
  const [on, setOn] = useState(false);
  const engines = useMemo(offeredEngines, []);
  const [engine, setEngine] = useState<WakeWordEngineName>('openwakeword');
  useEffect(() => {
    let current = true;
    loadWakeWordSwitch().then(saved => {
      if (current) {
        setOn(saved);
      }
    });
    loadWakeWordEngine().then(saved => {
      const picked = engines.find(name => name === saved);
      if (current && picked) {
        setEngine(picked);
      }
    });
    return () => {
      current = false;
    };
  }, [engines]);

  const [listenRequested, setListenRequested] = useState(false);
  const [chatVoiceBusy, setChatVoiceBusy] = useState(false);
  const [novaSpeaking, setNovaSpeaking] = useState(false);
  useEffect(() => {
    let tail: ReturnType<typeof setTimeout> | undefined;
    const sub = NovaVoiceModule.addSpeakingListener?.((speaking: boolean) => {
      clearTimeout(tail);
      if (speaking) {
        setNovaSpeaking(true);
      } else {
        tail = setTimeout(() => setNovaSpeaking(false), SPEECH_TAIL_MS);
      }
    });
    return () => {
      clearTimeout(tail);
      sub?.remove();
    };
  }, []);

  const handleWake = useCallback(() => {
    openForWake();
    setListenRequested(true);
  }, [openForWake]);
  const [handingOver, setHandingOver] = useState(false);
  const onListenRequestHandled = useCallback(() => {
    setListenRequested(false);
    setHandingOver(true);
  }, []);
  useEffect(() => {
    if (!handingOver) {
      return;
    }
    if (chatVoiceBusy) {
      setHandingOver(false);
      return;
    }
    const timer = setTimeout(() => setHandingOver(false), HANDOVER_MS);
    return () => clearTimeout(timer);
  }, [handingOver, chatVoiceBusy]);

  const wakeWord = useWakeWord({
    enabled: offered && on,
    paused: listenRequested || handingOver || chatVoiceBusy || novaSpeaking,
    onWake: handleWake,
    engine,
  });

  const toggle = useCallback(() => {
    setOn(previous => {
      saveWakeWordSwitch(!previous);
      return !previous;
    });
  }, []);

  /** The next engine in turn; the choice is kept on the device. */
  const switchEngine = useCallback(() => {
    setEngine(previous => {
      const next =
        engines[(engines.indexOf(previous) + 1) % engines.length] ?? previous;
      saveWakeWordEngine(next);
      return next;
    });
  }, [engines]);

  /** The switch for the chat header, where Hey Nova is offered. */
  const wakeWordSwitch = useMemo<NovaWakeWordSwitch | undefined>(
    () =>
      offered
        ? {
            on,
            blocked: wakeWord.state === 'blocked',
            waiting: wakeWord.state === 'waiting',
            onToggle: toggle,
            engine:
              engines.length > 1
                ? {label: WAKE_WORD_ENGINE_LABELS[engine], onSwitch: switchEngine}
                : undefined,
          }
        : undefined,
    [offered, on, wakeWord.state, toggle, engines, engine, switchEngine],
  );

  return {
    state: wakeWord.state,
    listenRequested,
    onListenRequestHandled,
    /** The chat window's mic or turn: Hey Nova steps aside meanwhile. */
    onVoiceBusyChange: setChatVoiceBusy,
    wakeWordSwitch,
  };
};
